import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './MyBookingsPage.css';

const MyBookingsPage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, upcoming, past
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    fetchBookings();
  }, [currentUser, navigate]);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      // The /user/:userId endpoint requires authentication (F-12 fix). Send
      // the Firebase ID token; the backend's verifyFirebaseToken middleware
      // resolves it to the authenticated user before the route handler runs.
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`/api/guest-bookings/user/${currentUser.uid}`, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch bookings');
      }
      const data = await response.json();
      setBookings(data.bookings || []);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    try {
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`/api/guest-bookings/${bookingId}/cancel`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to cancel booking');
      }

      // Refresh bookings
      fetchBookings();
    } catch (err) {
      console.error('Error canceling booking:', err);
      alert('Failed to cancel booking: ' + err.message);
    }
  };

  const handleShareLink = async (booking) => {
    if (!booking.guest_link_token) return;
    const url = `${window.location.origin}/booking/ref/${booking.guest_link_token}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(booking.booking_id);
      setTimeout(() => setCopiedId(null), 2500);
    } catch {
      window.prompt('Copy this link:', url);
    }
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getPaymentStatusBadge = (booking) => {
    if (booking?.status === 'cancelled') {
      return <span className="badge cancelled">Cancelled</span>;
    }
    if (booking?.payment_status === 'paid') {
      return <span className="badge paid">Paid</span>;
    }
    return <span className="badge pending">Pending</span>;
  };

  const hasCompletedPayment = (booking) => {
    const totalPaid = Number(booking?.total_paid_amount || 0);
    return booking?.payment_status === 'paid' || totalPaid > 0;
  };

  const getRefundDisplayValue = (booking) => {
    const refundAmount = Number(booking?.refund_amount || 0);
    return refundAmount > 0 ? `PKR ${refundAmount.toFixed(2)}` : 'As per cancellation policy';
  };

  const getBookingStatusBadge = (booking) => {
    const status = booking?.status;
    const statusClasses = {
      confirmed: 'confirmed',
      checked_in: 'checked-in',
      checked_out: 'checked-out',
      cancelled: 'cancelled',
    };

    const label = status === 'cancelled'
      ? (hasCompletedPayment(booking) ? 'cancelled (refund option)' : 'cancelled')
      : String(status || '').replace('_', ' ');

    return <span className={`badge ${statusClasses[status] || ''}`}>{label}</span>;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      easypaisa: 'Easypaisa',
      simulated: 'Simulated',
      cod: 'Cash on Delivery',
    };
    return labels[method] || method;
  };

  const isUpcoming = (checkInDate) => {
    return new Date(checkInDate) > new Date();
  };

  const isPast = (checkOutDate) => {
    return new Date(checkOutDate) < new Date();
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'upcoming') return isUpcoming(booking.check_in_date);
    if (filter === 'past') return isPast(booking.check_out_date);
    return true;
  });

  if (loading) {
    return (
      <div className="my-bookings-page">
        <div className="container">
          <div className="loading-state">Loading your bookings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-bookings-page">
      <div className="container">
        <div className="page-header">
          <h1>My Bookings</h1>
          <p>View and manage your hotel reservations</p>
        </div>

        {/* Filter Tabs */}
        <div className="booking-filters">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Bookings
          </button>
          <button 
            className={`filter-btn ${filter === 'upcoming' ? 'active' : ''}`}
            onClick={() => setFilter('upcoming')}
          >
            Upcoming
          </button>
          <button 
            className={`filter-btn ${filter === 'past' ? 'active' : ''}`}
            onClick={() => setFilter('past')}
          >
            Past
          </button>
        </div>

        {error && (
          <div className="error-banner">
            <p>{error}</p>
          </div>
        )}

        {/* Bookings List */}
        <div className="bookings-list">
          {filteredBookings.length === 0 ? (
            <div className="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" stroke="#ccc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="9 22 9 12 15 12 15 22" stroke="#ccc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <h3>No bookings found</h3>
              <p>You haven't made any bookings yet</p>
              <button className="btn-primary" onClick={() => navigate('/booking')}>
                Browse Hotels
              </button>
            </div>
          ) : (
            filteredBookings.map(booking => (
              <div key={booking.booking_id} className="booking-card">
                <div className="booking-header">
                  <div className="booking-id">
                    <span>Booking #{booking.booking_id}</span>
                    {getBookingStatusBadge(booking)}
                    {booking.booked_for_self === false && (
                      <span className="badge guest-booking" title={`Booked for ${booking.guest_name || 'another guest'}`}>
                        {booking.guest_name ? `For: ${booking.guest_name}` : 'For Guest'}
                      </span>
                    )}
                  </div>
                  <div className="booking-date">
                    Booked on {formatDate(booking.created_at)}
                  </div>
                </div>

                <div className="booking-body">
                  <div className="hotel-info">
                    <h3>{booking.hotel_name || 'Hotel Name'}</h3>
                    <p className="room-type">{booking.room_type || 'Room Type'}</p>
                  </div>

                  <div className="booking-details">
                    <div className="detail-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                        <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <span>{formatDate(booking.check_in_date)} - {formatDate(booking.check_out_date)}</span>
                    </div>

                    <div className="detail-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{booking.people} {booking.people === 1 ? 'Guest' : 'Guests'}</span>
                    </div>

                    {booking.special_requests && (
                      <div className="detail-item full-width">
                        <strong>Special Requests:</strong> {booking.special_requests}
                      </div>
                    )}
                  </div>

                  <div className="payment-details">
                    <div className="payment-row">
                      <span>Total Amount:</span>
                      <strong>PKR {parseFloat(booking.booking_price).toFixed(2)}</strong>
                    </div>
                    <div className="payment-row">
                      <span>Payment Method:</span>
                      <span>{getPaymentMethodLabel(booking.payment_method)}</span>
                    </div>
                    <div className="payment-row">
                      <span>Payment Status:</span>
                      {getPaymentStatusBadge(booking)}
                    </div>
                    {booking.status === 'cancelled' && hasCompletedPayment(booking) && (
                      <div className="payment-row">
                        <span>Refund Amount:</span>
                        <strong>{getRefundDisplayValue(booking)}</strong>
                      </div>
                    )}
                  </div>
                </div>

                <div className="booking-actions">
                  {booking.status === 'confirmed' && isUpcoming(booking.check_in_date) && (
                    <button 
                      className="btn-cancel" 
                      onClick={() => handleCancelBooking(booking.booking_id)}
                    >
                      Cancel Booking
                    </button>
                  )}
                  {booking.guest_link_token && (
                    <button
                      className="btn-share"
                      onClick={() => handleShareLink(booking)}
                      title="Copy guest check-in link"
                    >
                      {copiedId === booking.booking_id ? (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Copied!
                        </>
                      ) : (
                        <>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="16 6 12 2 8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="12" y1="2" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          Share
                        </>
                      )}
                    </button>
                  )}
                  <button 
                    className="btn-view" 
                    onClick={() => navigate(`/booking-details/${booking.booking_id}`)}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default MyBookingsPage;
